import { useState, useEffect } from "react";
import { Box, Typography, Card, Avatar, Rating, Stack, CircularProgress, Button } from "@mui/material";
import { useParams } from "react-router-dom";
import { toast } from "../../utilities/SnackBarUtils";

import axios from "axios";

const Vote = () => {
  const { eventId } = useParams();
  const [pitchConfig, setPitchConfig] = useState({enabled: false, voting: false});
  const [pitches, setPitches] = useState([]);
  const [title, setTitle] = useState("");
  const [votes, setVotes] = useState({});
  const [voterId, setVoterId] = useState(null);
  const [loading, setLoading] = useState(true);

  // initialize voterId when the event loads
  useEffect(() => {
    if (!eventId) return;

    let id = localStorage.getItem(`vote_${eventId}`);

    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(`vote_${eventId}`, id);
    }

    setVoterId(id);
  }, [eventId]);

  // fetch pitches once voterId is available
  useEffect(() => {
    if (!eventId || !voterId) return;
    getPitches();
  }, [eventId, voterId]);

  const getPitches = async () => {
    if (!voterId) return;

    try {
      setLoading(true);
      const { data } = await axios.get(
        `${process.env.REACT_APP_TNFT_SERVER}/pitches?eventId=${eventId}&voterId=${voterId}`,
      );

      if (data.success) {
        setPitchConfig({...data.pitchConfig});
        setPitches(data.pitches);
        setTitle(data.title);

        // initialize votes from score values returned on each pitch
        const voteMap = {};
        data.pitches.forEach((p) => {
          if (p.score) {
            voteMap[p._id] = p.score;
          }
        });

        setVotes(voteMap);
        setLoading(false);
      }
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  };

  const handleVote = async (pitchId, value) => {
    // MUI Rating sends null when a user clicks the same value again.
    // Prevent clearing the vote or sending 0 to the server.
    if (!value || value === votes[pitchId]) {
      return;
    }

    setVotes((prev) => ({
      ...prev,
      [pitchId]: value,
    }));

    const body = {
      event: eventId,
      pitch: pitchId,
      score: value,
      voterId,
    };

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_TNFT_SERVER}/pitches/vote`,
        body,
      );

      if (data.success) {
        toast.success("Your vote has been recorded!");
      } else {
        toast.error(data.error || "Unable to record your vote.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong while submitting your vote.");
    }
  };

  if (!eventId) {
    return (
      <Box
        sx={{
          maxWidth: 480,
          margin: "0 auto",
          padding: 3,
          textAlign: "center",
        }}
      >
        <Typography variant="h6">
          Ooops. Looks like there is nothing to see here
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={80}/>
      </Box>
    );
  }

  if (!pitchConfig?.enabled) {
    return (
      <Box
        sx={{
          maxWidth: 480,
          margin: "0 auto",
          padding: 3,
          textAlign: "center",
        }}
      >
        <Typography variant="h6">
          Pitches / voting is not set up for this event
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: 480,
        margin: "0 auto",
        padding: 3,
      }}
    >
      <Typography
        variant="h5"
        sx={{
          textAlign: "center",
          mb: 4,
        }}
      >
        {title}
      </Typography>

      <Stack spacing={4}>
        {pitches.map((pitch) => (
          <Card
            key={pitch._id}
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: 10,
            }}
          >
            <Avatar
              src={pitch.profileImage || ""}
              sx={{
                width: 120,
                height: 120,
                margin: "0 auto",
                mb: 2,
              }}
            >
              {(pitch.firstName || "").charAt(0)}
            </Avatar>

            <Typography variant="h6" sx={{ mb: pitch.socialHandle?.text && pitch.socialHandle?.link ? 0.5 : 2 }}>
              {pitch.firstName} {pitch.lastName}
            </Typography>

            {pitch.socialHandle?.text && pitch.socialHandle?.link && (
              <Typography variant="body2" sx={{ mb: 2 }}>
                <a
                  href={pitch.socialHandle.link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {pitch.socialHandle.text}
                </a>
              </Typography>
            )}
            <Typography variant="body2" sx={{ mb: 2 }}>
              {pitch?.summary}
            </Typography>

            {pitchConfig?.voting ? (
              <Rating
                size="large"
                value={votes[pitch._id] || 0}
                onChange={(e, value) => handleVote(pitch._id, value)}
              />
            ) : (
              <Button
                variant="contained"
                style={{width: 150, borderRadius: 30}}
                onClick={() => {
                  if (pitch.url) {
                    window.open(pitch.url, "_blank", "noopener,noreferrer");
                  }
                }}
              >
                Pick me
              </Button>
            )}
          </Card>
        ))}
      </Stack>
      <div style={{width: '100%', height: 100}} />
    </Box>
  );
};

export default Vote;
